import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";

import { Icon } from "@iconify/react";
import {
  Box,
  CircularProgress,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
} from "@mui/material";
import { GridColDef, GridRenderCellParams, GridRowId } from "@mui/x-data-grid";
import SearchField from "components/shared/SearchField/SearchField";
import { Link } from "react-router-dom";
import { INDEXING_STATUSES, SOURCE_TEAMS, TYPE_FOLDER } from "util/constants";
import { DocumentData } from "util/interfaces/DocumentData";

import { getBlobTree, getDocumentsWithChildren, performDownload } from "./DocumentGrid.utils";
import DocumentGridBreadcrumb from "./DocumentGridBreadcrumb";
import DocumentsGridSelected from "./DocumentsGridSelected";
import { usePostDownloadBlobs } from "@/api/usePostDownloadBlobs";
import { DocumentIcon } from "@/components/DocumentManagementView/DocumentGrid/DocumentIcon/DocumentIcon";
import CustomGrid from "@/components/shared/CustomGrid/CustomGrid";
import { CustomGridRef } from "@/components/shared/CustomGrid/types";
import { usePermissions } from "@/context/PermissionsContext";
import { Permission } from "@/utils/policies";

interface DocumentsGridProps {
  isDocumentsLoading: boolean;
  blobs: DocumentData[] | undefined;
  callbackAddFiles: (_modalName: "upload_file" | "create_folder") => void;
  callbackDeleteFile: (_files: DocumentData[]) => void;
  callbackMoveFiles: (_files: DocumentData[]) => void;
  callbackEditFile: (_file: DocumentData) => void;
  callbackOverrideFile: (_file: DocumentData) => void;
  setSelectedPath: Dispatch<SetStateAction<{ id: string; name: string }[]>>;
  selectedPath: { id: string; name: string }[];
  selectedRows: DocumentData[];
  setSelectedRows: Dispatch<SetStateAction<DocumentData[]>>;
}

const DocumentsGrid: React.FC<DocumentsGridProps> = ({
  setSelectedPath,
  selectedPath,
  isDocumentsLoading,
  blobs,
  callbackAddFiles,
  callbackDeleteFile,
  callbackEditFile,
  callbackOverrideFile,
  callbackMoveFiles,
  selectedRows,
  setSelectedRows,
}) => {
  const { hasPermission, hasAnyPermission } = usePermissions();
  const [documents, setDocuments] = useState<DocumentData[]>([]);
  const [hiddenItemIds, setHiddenItemIds] = useState<string[]>([]);
  const gridRef = useRef<CustomGridRef>(null);
  const { mutateAsync: downloadBlobs } = usePostDownloadBlobs();
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [downloadFromGridLoading, setDownloadFromGridLoading] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentData | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [selectedButtonId, setSelectedButtonId] = useState<GridRowId | null>(null);
  const [parentId, setParentId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const setLocalStorage = (row: any) => {
    localStorage.setItem(`doc-${row.id}`, JSON.stringify(row));
  };
  const theme = useTheme();

  const [gridRows, setGridRows] = useState<DocumentData[]>([]);

  useEffect(() => {
    if (gridRows.length === 0 && searchText === "") {
      setSelectedPath(selectedPath.slice(0, -1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridRows]);
  useEffect(() => {
    const allGridRows = documents.filter((b) => b.parentId === parentId);
    const rowsForGrid = searchText
      ? allGridRows.filter((file) =>
          file.documentName.toLowerCase().includes(searchText.toLowerCase())
        )
      : allGridRows;
    setGridRows(rowsForGrid);
  }, [documents, parentId, searchText]);
  const getNonFolderItems = (items: DocumentData[]) => {
    return items.filter((i) => i.fileType !== TYPE_FOLDER);
  };
  const columns = [
    {
      renderCell: (params: GridRenderCellParams<DocumentData>) => {
        const indexingStatus = INDEXING_STATUSES[params.row.indexing_status];
        return (
          <Tooltip title={params ? params.row.documentName : null} arrow>
            <Box className="flex align-items-center gap-8">
              <DocumentIcon fileType={params.row.fileType} />
              <Link
                style={{
                  color: theme.palette.primary.main,
                  textDecoration: "none",
                  maxWidth: "80%",
                }}
                data-testid={`link-${params.row.documentName}`}
                className="text-overflow-ellipsis"
                to={`/document-viewer?id=${params.row.id}`}
                onClick={(e) => {
                  if (params.row.fileType == TYPE_FOLDER) {
                    e.preventDefault();
                  }
                  handleColumnNameClick(params.row);
                }}
                onContextMenu={(e) => {
                  if (params.row.fileType == TYPE_FOLDER) {
                    e.preventDefault();
                  } else {
                    handleColumnNameClick(params.row);
                  }
                }}
                onMouseDown={(e) => {
                  if (e.button === 1) {
                    // Detection of the mouse scroll button
                    handleColumnNameClick(params.row);
                  }
                }}
                target="_blank"
              >
                {params.row.documentName}
              </Link>
              {params.row.source === SOURCE_TEAMS && (
                <Icon icon={`logos:microsoft-teams`} width={15} height={15} />
              )}
              {(params.row.indexing_status === "IN_PROGRESS" ||
                params.row.indexing_status === "ERROR") && (
                <>
                  <Divider orientation="vertical" style={{ height: 20 }} /> {indexingStatus}
                </>
              )}
            </Box>
          </Tooltip>
        );
      },
      headerName: "Document name",
      flex: 1.5,
      field: "documentName",
      sortable: true,
    },
    {
      field: "category",
      headerName: "Category",
      flex: 1,
      sortable: true,
      filterable: true,
    },
    {
      field: "fileType",
      headerName: "Type",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Box className="fileType_bullet_container">
          <Box
            className={`fileType_bullet ${params.row.fileType == TYPE_FOLDER ? "folder" : "file"}`}
          >
            {params.row.fileType}
          </Box>
        </Box>
      ),
      sortable: true,
      filterable: true,
    },
    {
      field: "source",
      headerName: "Source",
      flex: 1,
      sortable: true,
      filterable: true,
    },
    {
      field: "uploaded",
      headerName: "Uploaded",
      flex: 1,
      sortable: true,
    },
    {
      field: "uploadedBy",
      headerName: "Uploaded by",
      flex: 1,
      sortable: true,
      filterable: true,
    },
  ];
  // Use ref to track if we're updating selection internally to prevent loops
  const isUpdatingSelectionRef = useRef(false);
  const previousBlobsRef = useRef<DocumentData[] | undefined>(undefined);

  useEffect(() => {
    setSearchText("");
    if (blobs && blobs !== previousBlobsRef.current) {
      previousBlobsRef.current = blobs;
      const newDocuments = getBlobTree(blobs);
      setDocuments(newDocuments);

      // Preserve selection by finding the same documents in the new tree
      if (selectedRows.length > 0 && !isUpdatingSelectionRef.current) {
        const selectedIds = new Set(selectedRows.map((r) => r.id));
        const updatedSelection = newDocuments.filter((doc) => selectedIds.has(doc.id));

        if (updatedSelection.length !== selectedRows.length) {
          isUpdatingSelectionRef.current = true;
          setSelectedRows(updatedSelection);
          // Reset flag after state update
          globalThis.setTimeout(() => {
            isUpdatingSelectionRef.current = false;
          }, 0);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blobs]);
  const getFileInfo = (files: DocumentData[]) => {
    const response: { fileName: string | undefined; fileType: string } = {
      fileName: undefined,
      fileType: "zip",
    };
    if (files.length === 1 && files[0].fileType !== TYPE_FOLDER) {
      response.fileName = files[0].documentName;
      response.fileType = files[0].file_extension;
    }
    return response;
  };
  const filterOnlyUserSelected = (): DocumentData[] => {
    const toRemove: DocumentData[] = [];
    const filteredSelected: DocumentData[] = [];
    // Filter selected rows to avoid duplicates when folders and their children are both selected
    for (const selectedRow of selectedRows) {
      if (toRemove.some((sR) => sR.id === selectedRow.id)) {
        continue;
      }
      filteredSelected.push(selectedRow);
      // If this is a folder, keep it
      if (selectedRow.fileType === TYPE_FOLDER) {
        //Delete all the childs
        const descendants = removeDescendant(selectedRow);
        toRemove.push(...descendants);
        //Remove all the ascendant hierarchy
        const traversed = removeTraverse(selectedRow);
        toRemove.push(...traversed);
      } else if (selectedRow.fileType !== TYPE_FOLDER) {
        // If this is a file, check if any of its parent folders are also selected
        // We need to traverse up the hierarchy to see if any parent folder is selected
        const traversed = removeTraverse(selectedRow);
        toRemove.push(...traversed);
      }
    }
    return filteredSelected;
  };
  const createAndDownloadZip = async () => {
    setDownloadLoading(true);
    if (blobs) {
      try {
        const selectedFiles = filterOnlyUserSelected();
        const { data } = await downloadBlobs({
          blob_names: getNonFolderItems(selectedFiles).map((f) => ({
            name: f.documentName,
            uuid: f.uuid,
          })),
          folder_path: selectedFiles.filter((f) => f.fileType === TYPE_FOLDER).map((f) => f.path),
        });
        const { fileName, fileType } = getFileInfo(selectedFiles);
        performDownload(data, fileType, fileName);
      } catch {
        setDownloadLoading(false);
        return false;
      }
    }
    setDownloadLoading(false);
  };

  const handleDownloadFile = async () => {
    setDownloadFromGridLoading(true);
    if (selectedDocument) {
      try {
        const { data } = await downloadBlobs({
          blob_names:
            selectedDocument.fileType === TYPE_FOLDER
              ? []
              : [{ name: selectedDocument.documentName, uuid: selectedDocument.uuid }],
          folder_path: selectedDocument.fileType === TYPE_FOLDER ? [selectedDocument.path] : [],
        });
        const { fileName, fileType } = getFileInfo([selectedDocument]);
        performDownload(data, fileType, fileName);
      } catch {
        setDownloadFromGridLoading(false);
        return false;
      }
    }
    setDownloadFromGridLoading(false);
  };
  const handleColumnNameClick = (blob: DocumentData) => {
    if (blob.fileType == TYPE_FOLDER) {
      //Just add information to the breadcrumb
      setSelectedPath([...selectedPath, { id: blob.id, name: blob.documentName }]);
    } else {
      setLocalStorage(blob);
    }
  };
  useEffect(() => {
    if (selectedPath.length === 0) {
      setParentId(null);
    } else {
      const pathLength = selectedPath.length;
      setParentId(selectedPath[pathLength - 1].id);
    }
  }, [selectedPath]);

  const handleCustomGridSelectionChange = useCallback(
    (selectedRowsFromGrid: DocumentData[]) => {
      // Skip if we're updating selection internally
      if (isUpdatingSelectionRef.current) {
        return;
      }

      // Only update if the selection has actually changed - use IDs for comparison
      const newIds = new Set(selectedRowsFromGrid.map((r) => r.id));
      const currentIds = new Set(selectedRows.map((r) => r.id));

      // Check if sets are equal (same size and all elements match)
      const hasChanged =
        newIds.size !== currentIds.size || ![...newIds].every((id) => currentIds.has(id));

      if (hasChanged) {
        setSelectedRows(selectedRowsFromGrid);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRows, selectedPath]
  );
  const removeTraverse = (selectedRow: DocumentData) => {
    const toRemove: DocumentData[] = [];
    if (selectedRow.parentId) {
      let currentId: string | null = selectedRow.parentId;
      //We need to add all the ascendant hirerachy to remove
      while (currentId) {
        const found = selectedRows.find((rowParent) => rowParent.id === currentId);
        if (found) {
          toRemove.push(found);
          currentId = found.parentId;
        } else {
          currentId = null;
        }
      }
    }
    return toRemove;
  };
  const removeDescendant = (selectedRow: DocumentData) => {
    const toRemove: DocumentData[] = [];
    for (const childRow of selectedRows) {
      if (childRow.parentId === selectedRow.id) {
        if (childRow.fileType === TYPE_FOLDER) {
          toRemove.push(...removeDescendant(childRow));
        }
        toRemove.push(childRow);
      }
    }
    return toRemove;
  };
  const getColumns = (): GridColDef[] => {
    const toReturn: GridColDef[] = columns;
    if (
      hasAnyPermission([
        Permission.FOLDER_ACTION_RENAME,
        Permission.FOLDER_ACTION_DOWNLOAD,
        Permission.FOLDER_ACTION_MOVE_TO,
        Permission.FOLDER_ACTION_DELETE,
        Permission.FILE_ACTION_EDIT,
        Permission.FILE_ACTION_UPDATE_VERSION,
        Permission.FILE_ACTION_DOWNLOAD,
        Permission.FILE_ACTION_MOVE_TO,
        Permission.FILE_ACTION_DELETE,
      ])
    ) {
      toReturn.push({
        field: "actions",
        headerName: "Actions",
        flex: 0.5,
        renderCell: (params: GridRenderCellParams) => {
          const composedId = `Id-${params.row.id}`;
          const isSelected = selectedButtonId === params.id;
          const moveToFolderItem = (
            <MenuItem
              onClick={() => {
                callbackMoveFiles([params.row]);
              }}
              data-testid="open_moveFolder"
            >
              <ListItemText>Move to folder</ListItemText>
            </MenuItem>
          );
          return (
            <Box
              className="action_menu"
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                width: "100%",
              }}
            >
              <button
                data-testid={`menu_trigger-${params.row.id}`}
                id={composedId}
                onClick={(event) => {
                  setAnchorEl(event.currentTarget);
                  setSelectedDocument(params.row);
                  setSelectedButtonId(params.id);
                }}
                style={{
                  backgroundColor: isSelected ? theme.palette.primary.main : "transparent",
                  borderRadius: "5px",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0",
                }}
              >
                <Icon
                  icon="iconamoon:menu-kebab-vertical-bold"
                  width="25"
                  height="25"
                  color={isSelected ? "white" : theme.palette.primary.main}
                />
              </button>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={anchorEl?.id === composedId}
                onClose={() => {
                  setAnchorEl(null);
                  setSelectedButtonId(null);
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                slotProps={{
                  paper: {
                    "aria-labelledby": "basic-button",
                  },
                }}
                sx={{
                  "& .MuiPaper-root": {
                    boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.1)",
                    borderRadius: "0px",
                    transform: "translateX(30px)",
                  },
                }}
              >
                {params.row.fileType === TYPE_FOLDER && (
                  <>
                    {hasPermission(Permission.FOLDER_ACTION_RENAME) && (
                      <MenuItem
                        data-testid="open_renameFolder"
                        onClick={() => {
                          if (selectedDocument !== null) {
                            callbackEditFile(selectedDocument);
                          }
                        }}
                      >
                        <ListItemText>Rename folder</ListItemText>
                      </MenuItem>
                    )}

                    {hasPermission(Permission.FOLDER_ACTION_DOWNLOAD) && (
                      <MenuItem
                        data-testid="download_folder"
                        onClick={handleDownloadFile}
                        disabled={downloadFromGridLoading}
                      >
                        {downloadFromGridLoading && <CircularProgress size={15} />}
                        <ListItemText>
                          {downloadFromGridLoading ? "Downloading" : "Download folder"}{" "}
                        </ListItemText>
                      </MenuItem>
                    )}
                    {hasPermission(Permission.FOLDER_ACTION_MOVE_TO) && moveToFolderItem}
                    {hasPermission(Permission.FOLDER_ACTION_DELETE) && (
                      <MenuItem
                        data-testid="delete_folder"
                        onClick={() => {
                          if (selectedDocument) {
                            const filesTodelete = getNonFolderItems(
                              getDocumentsWithChildren([selectedDocument.id], documents)
                            );
                            callbackDeleteFile(filesTodelete);
                          }
                        }}
                      >
                        <ListItemText>Delete folder</ListItemText>
                      </MenuItem>
                    )}
                  </>
                )}

                {params.row.fileType !== TYPE_FOLDER && (
                  <>
                    {hasPermission(Permission.FILE_ACTION_EDIT) && (
                      <MenuItem
                        data-testid="open_modalEdit"
                        onClick={() => {
                          if (selectedDocument !== null) {
                            callbackEditFile(selectedDocument);
                          }
                        }}
                      >
                        <ListItemText>Edit</ListItemText>
                      </MenuItem>
                    )}
                    {hasPermission(Permission.FILE_ACTION_UPDATE_VERSION) && (
                      <MenuItem
                        data-testid="open_modalOverride"
                        onClick={() => {
                          if (selectedDocument !== null) {
                            callbackOverrideFile(selectedDocument);
                          }
                        }}
                      >
                        <ListItemText>Update file version</ListItemText>
                      </MenuItem>
                    )}
                    {hasPermission(Permission.FILE_ACTION_DOWNLOAD) && (
                      <MenuItem
                        data-testid="downloadButton"
                        onClick={handleDownloadFile}
                        disabled={downloadFromGridLoading}
                      >
                        {downloadFromGridLoading && <CircularProgress size={15} />}
                        <ListItemText>
                          {downloadFromGridLoading ? "Downloading" : "Download file"}{" "}
                        </ListItemText>
                      </MenuItem>
                    )}
                    {hasPermission(Permission.FILE_ACTION_MOVE_TO) && moveToFolderItem}
                    {hasPermission(Permission.FILE_ACTION_DELETE) && (
                      <MenuItem
                        data-testid="delete_btn"
                        onClick={() => {
                          if (selectedDocument) {
                            callbackDeleteFile([selectedDocument]);
                          }
                        }}
                      >
                        <ListItemText>Delete file</ListItemText>
                      </MenuItem>
                    )}
                  </>
                )}
              </Menu>
            </Box>
          );
        },
      });
    }
    return toReturn;
  };
  return (
    <Box className="flex flow-column" p={3} data-testid="documentGrid">
      <Box className="flex" justifyContent="space-between" mb={3}>
        <Box className="search-bar-container">
          <SearchField searchText={searchText} setSearchText={setSearchText} />
        </Box>
        <Box className="flex menu_items">
          <Tooltip arrow title="Clear filters applied to the Grid">
            <span>
              <button
                className="btn-default btn-default-style"
                onClick={() => {
                  if (gridRef?.current) {
                    gridRef.current.clearAllSortFilters();
                  }
                }}
                data-testid="clearFilters"
                style={{ color: theme.palette.primary.main }}
              >
                Clear all filters
              </button>
            </span>
          </Tooltip>
          {hasPermission(Permission.DOWNLOAD) && (
            <>
              <hr />
              <Tooltip arrow title="Download selected files/folders">
                <span>
                  <button
                    className="menu_btn menu_btn_download"
                    disabled={selectedRows.length === 0 || downloadLoading}
                    onClick={createAndDownloadZip}
                    data-testid="download_btn"
                  >
                    {downloadLoading ? (
                      <CircularProgress size={15} />
                    ) : (
                      <Icon icon="material-symbols:download" width={24} />
                    )}
                  </button>
                </span>
              </Tooltip>
            </>
          )}
          {hasPermission(Permission.MOVE_TO_FOLDER) && (
            <Tooltip arrow title="Move selected files/folders">
              <span>
                <button
                  className="menu_btn menu_btn_move"
                  onClick={() => {
                    const filteredSelected = filterOnlyUserSelected();
                    callbackMoveFiles(
                      documents.filter((row) => filteredSelected.find((sR) => sR.id === row.id))
                    );
                  }}
                  disabled={selectedRows.length === 0}
                  data-testid="moveButton"
                >
                  <Icon icon="tdesign:folder-move" width={24} />
                </button>
              </span>
            </Tooltip>
          )}
          {hasPermission(Permission.DELETE) && (
            <Tooltip arrow title="Delete selected files/folders">
              <span>
                <button
                  className="menu_btn menu_btn_delete"
                  onClick={() => {
                    if (blobs) {
                      callbackDeleteFile(getNonFolderItems(selectedRows));
                    }
                  }}
                  data-testid="deleteButton"
                  disabled={selectedRows.length === 0}
                >
                  <Icon icon="material-symbols:delete-outline-rounded" width={24} />
                </button>
              </span>
            </Tooltip>
          )}

          {hasPermission(Permission.ADD_FOLDER) && (
            <>
              <hr />
              <button
                onClick={() => callbackAddFiles("create_folder")}
                disabled={selectedRows.length >= 1}
                className="upload_btn"
                data-testid="createFolderButton"
              >
                Add folder
              </button>
            </>
          )}
          {hasPermission(Permission.UPLOAD_FILES) && (
            <button
              onClick={() => callbackAddFiles("upload_file")}
              disabled={selectedRows.length >= 1}
              className="upload_btn"
              data-testid="uploadFileButton"
            >
              Upload files
            </button>
          )}
        </Box>
      </Box>
      <DocumentGridBreadcrumb breadcrumbs={selectedPath} clickOnPathCallback={setSelectedPath} />
      <Box className="flex grid-body-content" sx={{ height: "calc(100vh - 383px)" }}>
        <CustomGrid
          ref={gridRef}
          style={{
            maxHeight: "60vh",
            minHeight: "505px",
            width: "70vw",
            overflow: "visible",
          }}
          rows={gridRows}
          loading={isDocumentsLoading}
          columns={getColumns()}
          onSelectionChange={handleCustomGridSelectionChange}
          initialSelectedRowIds={selectedRows.map((r) => r.id)}
          initialExplicitlyDeselectedIds={hiddenItemIds}
          allRows={documents}
          keepNonExistentRowsSelected
          pageSizeOptions={[5, 10, 50, 100]}
          checkboxSelection={hasPermission(Permission.CHECKBOXES)}
          disableRowSelectionOnClick
          classes={{
            footerContainer: selectedRows.length > 0 ? "custom-footer" : "footer",
          }}
        />
        {selectedRows.length > 0 && (
          <Box className="flex flow-column selected-side" data-testid="selectedFiles">
            <Box className="selected-side-header">
              <h3>Selected files</h3>
            </Box>
            <Box className="flex flow-column gap-5 selected-side-content">
              <DocumentsGridSelected
                selectedRows={selectedRows.map((r: DocumentData) => r.id)}
                allRows={documents}
                callbackSetSelectedRows={(idsToRemove: string[]) => {
                  setSelectedRows(selectedRows.filter((r) => !idsToRemove.includes(r.id)));
                }}
                onHiddenItemsChange={(hiddenIds: string[]) => {
                  setHiddenItemIds(hiddenIds);
                }}
              />
              <Box className="reset_btn_container">
                <button onClick={() => setSelectedRows([])} className="clear_btn_manag">
                  <h4>Clear</h4>
                </button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DocumentsGrid;
